import React, { useEffect, useState } from "react";
import { XMarkIcon, ArrowPathIcon, PlusIcon, TrashIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { createTariff, updateTariff } from "../../api/tariffsAPI";

export const LANGS = [
  { key: "", label: "RU" },
  { key: "_kk", label: "KZ" },
  { key: "_en", label: "EN" },
  { key: "_cn", label: "CN" },
];

// Ключи и значения по умолчанию — зеркало TARIFF_CONDITION_DEFAULTS на backend
export const CONDITION_FIELDS = [
  { key: "vip_hours", label: "VIP-часы", hint: "Пакет часов VIP-показа объявлений", type: "int" },
  { key: "max_active_announcements", label: "Активных объявлений", hint: "0 — без ограничений", type: "int" },
  { key: "max_photos_per_ad", label: "Фото в объявлении", type: "int" },
  { key: "top_placement_slots", label: "Слотов в топе", type: "int" },
  { key: "search_boost_multiplier", label: "Буст в поиске, ×", type: "float" },
  { key: "tiktok_videos", label: "TikTok-видео", hint: "Только для описания, система не считает", type: "int" },
  { key: "support_priority", label: "Поддержка", type: "select", options: [["standard", "Обычная"], ["fast", "Быстрая"], ["instant", "Мгновенная"]] },
  { key: "banner_included", label: "Баннер на главной включён", type: "bool" },
  { key: "analytics_enabled", label: "Аналитика", type: "bool" },
  { key: "personal_manager", label: "Персональный менеджер", type: "bool" },
  { key: "brand_showcase", label: "Витрина бренда", type: "bool" },
  { key: "negotiable_price", label: "Цена по договорённости («Спец контент»)", type: "bool" },
  { key: "auto_renew", label: "Автопродление (метка)", type: "bool" },
  { key: "search_placement", label: "В поиске (метка)", type: "bool" },
  { key: "home_placement", label: "На главной (метка)", type: "bool" },
];

const CONDITION_DEFAULTS = {
  vip_hours: 0,
  max_active_announcements: 3,
  max_photos_per_ad: 5,
  top_placement_slots: 0,
  search_boost_multiplier: 1,
  tiktok_videos: 0,
  support_priority: "standard",
  banner_included: false,
  analytics_enabled: false,
  personal_manager: false,
  brand_showcase: false,
  negotiable_price: false,
  auto_renew: false,
  search_placement: false,
  home_placement: false,
};

export const ICONS = [
  ["star", "☆ Звезда"],
  ["star_filled", "★ Звезда (заливка)"],
  ["medal_star", "🏅 Медаль"],
];

const inputCls =
  "w-full rounded-lg border border-border-primary bg-bg-primary px-3 py-2 text-sm text-text-primary " +
  "placeholder:text-text-muted focus:border-[#4F45E5] focus:outline-none focus:ring-2 focus:ring-[#4F45E5]/30";

const emptyFeature = { text: "", text_kk: "", text_en: "", text_cn: "" };

export const blankTariff = (forUserType) => ({
  name: "",
  name_kk: "",
  name_en: "",
  name_cn: "",
  icon: "star",
  duration: "1 месяц",
  duration_kk: "1 ай",
  duration_en: "1 month",
  duration_cn: "1个月",
  price: "",
  price_value: 0,
  currency: "₸",
  promotion_type: 0,
  is_popular: false,
  features: [],
  conditions: { ...CONDITION_DEFAULTS },
  for_user_type: forUserType || "business",
  commission_rate: null,
  is_active: true,
  is_archived: false,
  available_until: "",
  sort_order: 0,
  duration_days: 30,
});

const toForm = (t) => ({
  ...blankTariff(t.for_user_type),
  ...t,
  conditions: { ...CONDITION_DEFAULTS, ...(t.conditions || {}) },
  features: (t.features || []).map(({ text, text_kk, text_en, text_cn }) => ({ text, text_kk, text_en, text_cn })),
  available_until: t.available_until || "",
  commission_rate: t.commission_rate ?? null,
});

const Field = ({ label, hint, children, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="mb-1 block text-xs font-medium text-text-secondary">{label}</span>
    {children}
    {hint && <span className="mt-0.5 block text-[11px] text-text-muted">{hint}</span>}
  </label>
);

const Section = ({ title, children }) => (
  <section className="space-y-3 border-t border-border-primary pt-5 first:border-0 first:pt-0">
    <h4 className="text-sm font-semibold text-text-primary">{title}</h4>
    {children}
  </section>
);

const formatPrice = (value, currency) => `${Number(value || 0).toLocaleString("ru-RU")} ${currency || "₸"}`;

const TariffEditor = ({ tariff, defaultUserType, onClose, onSaved }) => {
  const editing = Boolean(tariff?.id);
  const [form, setForm] = useState(() => (tariff ? toForm(tariff) : blankTariff(defaultUserType)));
  const [lang, setLang] = useState("");
  const [priceTouched, setPriceTouched] = useState(Boolean(tariff?.price));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const setCond = (key, value) => setForm((f) => ({ ...f, conditions: { ...f.conditions, [key]: value } }));

  // Текст цены по умолчанию формируется из числа, пока его не правили вручную
  useEffect(() => {
    if (!priceTouched) setForm((f) => ({ ...f, price: formatPrice(f.price_value, f.currency) }));
  }, [form.price_value, form.currency, priceTouched]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && !saving && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [saving, onClose]);

  const validate = () => {
    for (const l of LANGS) {
      if (!form[`name${l.key}`]?.trim()) return `Заполните название (${l.label}).`;
      if (!form[`duration${l.key}`]?.trim()) return `Заполните срок (${l.label}).`;
    }
    if (!(Number(form.price_value) >= 0)) return "Цена должна быть числом.";
    if (!(Number(form.duration_days) >= 1)) return "Срок в днях должен быть не меньше 1.";
    if (form.features.some((f) => LANGS.some((l) => !f[`text${l.key}`]?.trim()))) return "Заполните все переводы пунктов описания.";
    return null;
  };

  const save = async (e) => {
    e.preventDefault();
    const problem = validate();
    if (problem) return setError(problem);
    setSaving(true);
    setError(null);
    const body = {
      ...Object.fromEntries(LANGS.flatMap((l) => [[`name${l.key}`, form[`name${l.key}`].trim()], [`duration${l.key}`, form[`duration${l.key}`].trim()]])),
      icon: form.icon,
      price: form.price.trim() || formatPrice(form.price_value, form.currency),
      price_value: Number(form.price_value),
      currency: form.currency || "₸",
      promotion_type: Number(form.promotion_type) || 0,
      is_popular: form.is_popular,
      features: form.features,
      conditions: Object.fromEntries(
        CONDITION_FIELDS.map(({ key, type }) => [key, type === "int" ? parseInt(form.conditions[key], 10) || 0 : type === "float" ? Number(form.conditions[key]) || 0 : form.conditions[key]])
      ),
      for_user_type: form.for_user_type,
      commission_rate: form.for_user_type === "business" && form.commission_rate !== null && form.commission_rate !== "" ? Number(form.commission_rate) : null,
      is_active: form.is_active,
      is_archived: form.is_archived,
      available_until: form.available_until || null,
      sort_order: parseInt(form.sort_order, 10) || 0,
      duration_days: parseInt(form.duration_days, 10) || 30,
    };
    try {
      const saved = editing ? await updateTariff(tariff.id, body) : await createTariff(body);
      onSaved(saved, editing ? `Тариф «${body.name}» сохранён` : `Тариф «${body.name}» создан`);
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/60" role="presentation" onMouseDown={(e) => e.target === e.currentTarget && !saving && onClose()}>
      <form onSubmit={save} role="dialog" aria-modal="true" className="flex h-full w-full max-w-3xl flex-col bg-bg-card text-left shadow-2xl">
        <div className="flex items-center justify-between border-b border-border-primary px-6 py-4">
          <div>
            <h3 className="text-lg font-semibold text-text-primary">{editing ? `Тариф #${tariff.id}` : "Новый тариф"}</h3>
            <p className="text-xs text-text-secondary">Изменения сразу видны в приложении и на сайте</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Закрыть (Esc)" className="rounded-lg p-1.5 text-text-muted hover:bg-bg-tertiary hover:text-text-primary">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5">
          <Section title="Основное">
            <div className="flex flex-wrap gap-2">
              {[
                ["business", "Для бизнеса"],
                ["individual", "Для физ. лиц"],
              ].map(([v, l]) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => set("for_user_type", v)}
                  className={`rounded-full border px-3 py-1 text-xs font-semibold ${form.for_user_type === v ? "border-[#4F45E5]/50 bg-[#4F45E5]/15 text-text-primary" : "border-border-primary text-text-secondary hover:bg-bg-tertiary"}`}
                >
                  {l}
                </button>
              ))}
            </div>

            <div>
              <div className="mb-2 flex gap-1">
                {LANGS.map((l) => {
                  const missing = !form[`name${l.key}`]?.trim() || !form[`duration${l.key}`]?.trim();
                  return (
                    <button
                      key={l.label}
                      type="button"
                      onClick={() => setLang(l.key)}
                      className={`relative rounded-md px-2.5 py-1 text-xs font-semibold ${lang === l.key ? "bg-bg-tertiary text-text-primary" : "text-text-secondary hover:text-text-primary"}`}
                    >
                      {l.label}
                      {missing && <span className="absolute -right-0.5 -top-0.5 h-1.5 w-1.5 rounded-full bg-rose-500" title="Не заполнено" />}
                    </button>
                  );
                })}
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field label={`Название (${LANGS.find((l) => l.key === lang).label})`}>
                  <input value={form[`name${lang}`] || ""} onChange={(e) => set(`name${lang}`, e.target.value)} className={inputCls} />
                </Field>
                <Field label={`Срок — текст (${LANGS.find((l) => l.key === lang).label})`}>
                  <input value={form[`duration${lang}`] || ""} onChange={(e) => set(`duration${lang}`, e.target.value)} className={inputCls} />
                </Field>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Field label="Иконка">
                <select value={form.icon} onChange={(e) => set("icon", e.target.value)} className={inputCls}>
                  {ICONS.map(([v, l]) => (
                    <option key={v} value={v}>{l}</option>
                  ))}
                </select>
              </Field>
              <Field label="Уровень" hint="Оформление карточки">
                <select value={form.promotion_type} onChange={(e) => set("promotion_type", Number(e.target.value))} className={inputCls}>
                  <option value={0}>0 — базовый</option>
                  <option value={1}>1 — средний</option>
                  <option value={2}>2 — премиум</option>
                </select>
              </Field>
              <Field label="Порядок" hint="Меньше — выше">
                <input type="number" value={form.sort_order} onChange={(e) => set("sort_order", e.target.value)} className={inputCls} />
              </Field>
              <label className="flex items-end gap-2 pb-2 text-sm text-text-primary">
                <input type="checkbox" checked={form.is_popular} onChange={(e) => set("is_popular", e.target.checked)} className="h-4 w-4 accent-[#4F45E5]" />
                «Популярный»
              </label>
            </div>
          </Section>

          <Section title="Цена и срок">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Field label="Цена, число">
                <input type="number" min="0" step="1" value={form.price_value} onChange={(e) => set("price_value", e.target.value)} className={inputCls} />
              </Field>
              <Field label="Валюта">
                <input value={form.currency} onChange={(e) => set("currency", e.target.value)} className={inputCls} />
              </Field>
              <Field label="Цена, текст" hint="Как показывать">
                <input
                  value={form.price}
                  onChange={(e) => {
                    setPriceTouched(true);
                    set("price", e.target.value);
                  }}
                  className={inputCls}
                />
              </Field>
              <Field label="Срок, дней" hint="Длительность подписки">
                <input type="number" min="1" value={form.duration_days} onChange={(e) => set("duration_days", e.target.value)} className={inputCls} />
              </Field>
            </div>
            {form.for_user_type === "business" && (
              <Field label="Комиссия Agroland с продаж, %" hint="Пусто — общая ставка по умолчанию (16%)" className="max-w-xs">
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={form.commission_rate ?? ""}
                  onChange={(e) => set("commission_rate", e.target.value === "" ? null : e.target.value)}
                  placeholder="16"
                  className={inputCls}
                />
              </Field>
            )}
          </Section>

          <Section title="Условия тарифа">
            <p className="-mt-2 text-xs text-text-muted">Эти значения применяются системой — лимиты объявлений, VIP-часы при покупке и т.д.</p>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {CONDITION_FIELDS.filter((c) => c.type !== "bool").map((c) => (
                <Field key={c.key} label={c.label} hint={c.hint}>
                  {c.type === "select" ? (
                    <select value={form.conditions[c.key]} onChange={(e) => setCond(c.key, e.target.value)} className={inputCls}>
                      {c.options.map(([v, l]) => (
                        <option key={v} value={v}>{l}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="number"
                      min="0"
                      step={c.type === "float" ? "0.1" : "1"}
                      value={form.conditions[c.key]}
                      onChange={(e) => setCond(c.key, e.target.value)}
                      className={inputCls}
                    />
                  )}
                </Field>
              ))}
            </div>
            <div className="grid grid-cols-1 gap-x-4 gap-y-1 sm:grid-cols-2">
              {CONDITION_FIELDS.filter((c) => c.type === "bool").map((c) => (
                <label key={c.key} className="flex cursor-pointer items-center gap-2 py-1 text-sm text-text-primary">
                  <input type="checkbox" checked={Boolean(form.conditions[c.key])} onChange={(e) => setCond(c.key, e.target.checked)} className="h-4 w-4 accent-[#4F45E5]" />
                  {c.label}
                </label>
              ))}
            </div>
          </Section>

          <Section title="Пункты описания в приложении">
            {form.features.length === 0 && <p className="text-xs text-text-muted">Пунктов нет. Они показываются списком в карточке тарифа.</p>}
            <ul className="space-y-2">
              {form.features.map((f, i) => (
                <li key={i} className="rounded-lg border border-border-primary p-2">
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {LANGS.map((l) => (
                      <input
                        key={l.label}
                        value={f[`text${l.key}`]}
                        onChange={(e) => set("features", form.features.map((x, j) => (j === i ? { ...x, [`text${l.key}`]: e.target.value } : x)))}
                        placeholder={l.label}
                        aria-label={`Пункт ${i + 1}, ${l.label}`}
                        className={`${inputCls} py-1.5`}
                      />
                    ))}
                  </div>
                  <button type="button" onClick={() => set("features", form.features.filter((_, j) => j !== i))} className="mt-1.5 inline-flex items-center gap-1 text-xs text-text-muted hover:text-rose-500">
                    <TrashIcon className="h-3.5 w-3.5" /> Удалить пункт
                  </button>
                </li>
              ))}
            </ul>
            <button type="button" onClick={() => set("features", [...form.features, { ...emptyFeature }])} className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-border-primary px-3 py-1.5 text-sm text-text-secondary hover:bg-bg-tertiary">
              <PlusIcon className="h-4 w-4" /> Добавить пункт
            </button>
          </Section>

          <Section title="Продажа">
            <div className="space-y-1">
              <label className="flex cursor-pointer items-center gap-2 text-sm text-text-primary">
                <input type="checkbox" checked={form.is_active} onChange={(e) => set("is_active", e.target.checked)} className="h-4 w-4 accent-[#4F45E5]" />
                Продаётся (видна пользователям)
              </label>
              <label className="flex cursor-pointer items-center gap-2 text-sm text-text-primary">
                <input type="checkbox" checked={form.is_archived} onChange={(e) => set("is_archived", e.target.checked)} className="h-4 w-4 accent-[#4F45E5]" />
                Архивная линейка (показывается в разделе «Архив»)
              </label>
            </div>
            <Field label="Продаётся до" hint="После этой даты тариф автоматически пропадёт из продажи. Пусто — бессрочно." className="max-w-xs">
              <input type="date" value={form.available_until || ""} onChange={(e) => set("available_until", e.target.value)} className={inputCls} />
            </Field>
            {editing && tariff.is_active && !form.is_active && (
              <p className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-500">
                Тариф пропадёт из продажи. Действующие подписки сохранятся до окончания срока.
              </p>
            )}
          </Section>
        </div>

        <div className="border-t border-border-primary px-6 py-4">
          {error && (
            <p role="alert" className="mb-3 flex items-start gap-2 rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-500">
              <ExclamationTriangleIcon className="h-5 w-5 shrink-0" />
              {error}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} disabled={saving} className="rounded-lg border border-border-primary px-4 py-2 text-sm font-medium text-text-secondary hover:bg-bg-tertiary">
              Отмена
            </button>
            <button type="submit" disabled={saving} className="inline-flex items-center gap-2 rounded-lg bg-accent-grad px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50">
              {saving && <ArrowPathIcon className="h-4 w-4 animate-spin" />}
              {editing ? "Сохранить" : "Создать тариф"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default TariffEditor;
